import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { Menu } from "lucide-react"
import * as React from "react"

import { cn } from "@/lib/utils"

import { NavVisibilityProvider } from "./nav-visibility"
import { Sidebar, type NavGroupDef, type NavRole } from "./sidebar"

/**
 * Holds the sidebar + a single scroll region + three invariants a page
 * should not have to think about:
 *
 * 1. Route-aware collapse with manual override winning and persisting —
 *    a layout that keeps re-deciding what you already decided is worse
 *    than one that never helps.
 * 2. Route changes are announced to screen readers via a polite live
 *    region; a single-page app otherwise repaints everything silently.
 * 3. The scroll region is the main element, not the body. The workspace
 *    needs its own internal scrolling with a fixed header; if the body
 *    scrolls, the split pane cannot size itself.
 */

const SIDEBAR_STORAGE_KEY_DEFAULT = "pyxis:sidebar-collapsed"
const SIDEBAR_STORAGE_KEY_RAIL = "pyxis:sidebar-collapsed-rail"
const SIDEBAR_STORAGE_KEY_HIDDEN = "pyxis:sidebar-hidden"

export interface AppShellProps {
  nav: NavGroupDef[]
  role: NavRole
  /** Nav item id of the current route. */
  activeId: string
  /** For the announcement and the document title. */
  routeTitle: string
  /** Routes that default to the rail. */
  railRoutes?: string[]
  currentHref?: string
  user: { name: string; email: string; roleLabel: string }
  theme: "light" | "dark"
  onThemeChange: (t: "light" | "dark") => void
  onNavigate?: (href: string) => void
  onSignOut?: () => void
  /** The workspace supplies its own header, so the shell omits the top bar. */
  hideTopBar?: boolean
  children: React.ReactNode
}

export function AppShell({
  nav,
  role,
  activeId,
  routeTitle,
  // A run's results and its labels (`/evaluations/...`) want the width, as a review does.
  railRoutes = ["/reviews/", "/evaluations/"],
  currentHref = "",
  user,
  theme,
  onThemeChange,
  onNavigate,
  onSignOut,
  hideTopBar = false,
  children,
}: AppShellProps) {
  // Two memories, not one: preference on rail routes and preference
  // elsewhere. So expanding the nav to browse queues (non-rail) doesn't
  // then override the collapse the user prefers deep inside a review
  // (rail). The workspace mode change resets the intent — the user is
  // choosing between two work postures, not one global toggle.
  const [manualDefault, setManualDefault] = React.useState<boolean | null>(() => {
    if (typeof window === "undefined") return null
    const v = window.localStorage.getItem(SIDEBAR_STORAGE_KEY_DEFAULT)
    return v === null ? null : v === "1"
  })
  const [manualRail, setManualRail] = React.useState<boolean | null>(() => {
    if (typeof window === "undefined") return null
    const v = window.localStorage.getItem(SIDEBAR_STORAGE_KEY_RAIL)
    return v === null ? null : v === "1"
  })
  const [mobileOpen, setMobileOpen] = React.useState(false)

  // A third state beyond expanded/collapsed: gone. Comparing two documents
  // side by side wants the full width of the window, and a 56px rail of app
  // chrome is the first thing a reader would trade away for it. Remembered
  // globally rather than per route — hiding the navigation is a statement
  // about how this person works, not about where they happen to be.
  const [navHidden, setNavHidden] = React.useState(() => {
    if (typeof window === "undefined") return false
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY_HIDDEN) === "1"
  })
  const setNavHiddenPersisted = React.useCallback((hidden: boolean) => {
    setNavHidden(hidden)
    window.localStorage.setItem(SIDEBAR_STORAGE_KEY_HIDDEN, hidden ? "1" : "0")
  }, [])

  const routeWantsRail = railRoutes.some((r) => currentHref.startsWith(r))
  const collapsed = routeWantsRail ? (manualRail ?? true) : (manualDefault ?? false)

  const setCollapsed = (c: boolean) => {
    if (routeWantsRail) {
      setManualRail(c)
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY_RAIL, c ? "1" : "0")
    } else {
      setManualDefault(c)
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY_DEFAULT, c ? "1" : "0")
    }
  }

  // Close the mobile drawer on navigation — otherwise it stays open over
  // the page the user just asked for.
  React.useEffect(() => {
    setMobileOpen(false)
  }, [currentHref])

  // Auto-collapse on entering a rail route (review workspace). The
  // reviewer wants the PDF, not the app chrome — and if they explicitly
  // expand while in-review we honor it for the rest of that visit, but
  // the next arrival still collapses. Tracked via a per-route memo so
  // navigating between reviews doesn't repeatedly override an intent
  // the user already set within the current review session.
  const lastRailHrefRef = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (routeWantsRail && lastRailHrefRef.current !== currentHref) {
      lastRailHrefRef.current = currentHref
      setManualRail(true)
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY_RAIL, "1")
    } else if (!routeWantsRail) {
      lastRailHrefRef.current = null
    }
  }, [currentHref, routeWantsRail])

  React.useEffect(() => {
    document.title = `${routeTitle} · Vela`
  }, [routeTitle])

  return (
    <TooltipPrimitive.Provider delayDuration={300}>
      {/* On paper the frame lets go: the page prints at its full length, not
          clipped to one screen of a scroll area, and without the app's chrome. */}
      <div className="flex h-dvh overflow-hidden bg-bg print:block print:h-auto print:overflow-visible">
        <a
          href="#main"
          className={cn(
            "sr-only focus:not-sr-only",
            "focus:fixed focus:left-3 focus:top-3 focus:z-toast",
            "focus:rounded-md focus:bg-text focus:px-3 focus:py-2",
            "focus:text-body-sm focus:text-[color:var(--surface)]",
          )}
        >
          Skip to content
        </a>

        <div className={cn("hidden md:block print:hidden", navHidden && "md:hidden")}>
          <Sidebar
            nav={nav}
            role={role}
            activeId={activeId}
            collapsed={collapsed}
            onCollapsedChange={setCollapsed}
            user={user}
            theme={theme}
            onThemeChange={onThemeChange}
            onNavigate={onNavigate}
            onSignOut={onSignOut}
          />
        </div>

        {mobileOpen && (
          <div className="fixed inset-0 z-drawer md:hidden">
            <div
              className="animate-in fade-in-0 absolute inset-0 bg-[rgb(18_22_28/0.35)] duration-180"
              onClick={() => {
                setMobileOpen(false)
              }}
            />
            <div className="animate-in slide-in-from-left duration-260 ease-out absolute inset-y-0 left-0">
              <Sidebar
                nav={nav}
                role={role}
                activeId={activeId}
                collapsed={false}
                onCollapsedChange={() => {
                  setMobileOpen(false)
                }}
                user={user}
                theme={theme}
                onThemeChange={onThemeChange}
                onNavigate={onNavigate}
                onSignOut={onSignOut}
              />
            </div>
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col print:block">
          {!hideTopBar && (
            <TopBar
              title={routeTitle}
              onOpenMenu={() => {
                setMobileOpen(true)
              }}
            />
          )}

          <main id="main" className="min-h-0 flex-1 overflow-y-auto print:overflow-visible">
            <NavVisibilityProvider value={{ hidden: navHidden, setHidden: setNavHiddenPersisted }}>
              {children}
            </NavVisibilityProvider>
          </main>
        </div>

        {/* Announces route changes to screen readers. */}
        <div aria-live="polite" className="sr-only">
          {routeTitle}
        </div>
      </div>
    </TooltipPrimitive.Provider>
  )
}

/**
 * Deliberately thin. Most screens carry their own heading and actions in
 * the page body, so a second bar repeating the title would be chrome for
 * its own sake. The workspace passes `hideTopBar` — it supplies its own
 * header with the view switcher and drawer buttons.
 */

export function TopBar({ title, onOpenMenu }: { title: string; onOpenMenu: () => void }) {
  return (
    <header className="flex h-12 shrink-0 items-center gap-2.5 border-b border-border bg-surface px-3.5 md:hidden print:hidden">
      <button
        type="button"
        aria-label="Open navigation"
        onClick={onOpenMenu}
        className="grid size-8 place-items-center rounded-md text-text-2 hover:bg-surface-sunken [&_svg]:size-4.5"
      >
        <Menu />
      </button>

      <span className="truncate text-body-sm font-medium text-text">{title}</span>

      <span className="flex-1" />
    </header>
  )
}

/**
 * 403 and 404, which the shell owns because they are shell states rather
 * than page states. With a role-aware sidebar the 403 is rare — you
 * cannot click what you cannot see — but a bookmarked or shared URL
 * still reaches it.
 */

export function RouteError({
  kind,
  roleLabel,
  onBack,
  backLabel = "Back to the review queue",
  backPending = false,
}: {
  kind: 403 | 404
  roleLabel?: string
  onBack?: () => void
  /** Where the button goes, in words; the caller decides the destination. */
  backLabel?: string
  /** The destination is still being worked out: the button waits rather than guessing. */
  backPending?: boolean
}) {
  return (
    <div className="mx-auto flex max-w-[52ch] flex-col items-center px-6 py-24 text-center">
      <span className="font-id text-[64px] leading-none tracking-[-0.03em] text-border-strong">
        {kind}
      </span>
      <h1 className="mt-4 text-title">{kind === 403 ? "Not yours to open" : "Page not found"}</h1>
      <p className="mt-2.5 text-body leading-normal text-text-2">
        {kind === 403 ? (
          <>
            Your account is a <b>{roleLabel ?? "viewer"}</b>, and this screen is not part of that
            role. If you need it, an administrator can change your role — nothing here is broken.
          </>
        ) : (
          <>The address may be mistyped or out of date.</>
        )}
      </p>
      <button
        type="button"
        onClick={onBack}
        disabled={backPending}
        aria-busy={backPending || undefined}
        className="mt-5 rounded-md bg-brand px-3.5 py-2 text-body-sm font-semibold text-white transition-colors duration-120 hover:bg-brand-strong disabled:pointer-events-none disabled:opacity-60"
      >
        {backLabel}
      </button>
    </div>
  )
}
